<script lang="ts">
  import type {
    Units,
    RegMarkStyle,
    MirrorMode,
    CornerKey,
    TileMode,
  } from '$lib/model/types';
  import { projectStore } from '$lib/state/project.svelte';
  import { walkLeaves } from '$lib/model/tree';
  import {
    DPI_PRESETS,
    MESH_PRESETS,
    lpiCeiling,
    pixelDimensions,
  } from '$lib/compute/settings-utils';
  import { computeTiles } from '$lib/compute/tiling';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';
  import Slider from './Slider.svelte';
  import ColorPicker from './ColorPicker.svelte';

  const leafCount = $derived(walkLeaves(projectStore.pipeline).length);
  const ceiling = $derived(lpiCeiling(projectStore.settings));
  const dims = $derived(pixelDimensions(projectStore.settings));
  const tiles = $derived(computeTiles(projectStore.settings));

  $effect(() => {
    if (projectStore.settings.reg_marks_auto) {
      const want: RegMarkStyle = leafCount >= 2 ? 'cross' : 'none';
      if (projectStore.settings.reg_marks !== want) {
        projectStore.settings.reg_marks = want;
        triggerRecompute();
      }
    }
  });

  function onUnitsChange(e: Event) {
    const newUnits = (e.target as HTMLSelectElement).value as Units;
    const old = projectStore.settings.units;
    if (old === newUnits) return;
    const w = projectStore.settings.target_width;
    const h = projectStore.settings.target_height;
    const factor = unitFactor(old, newUnits);
    projectStore.settings.units = newUnits;
    projectStore.settings.target_width = round2(w * factor);
    projectStore.settings.target_height = round2(h * factor);
    triggerRecompute();
  }

  function unitFactor(from: Units, to: Units): number {
    const toIn = from === 'in' ? 1 : from === 'cm' ? 1 / 2.54 : 1 / 25.4;
    const fromIn = to === 'in' ? 1 : to === 'cm' ? 2.54 : 25.4;
    return toIn * fromIn;
  }
  function round2(n: number): number {
    return Math.round(n * 100) / 100;
  }
  function onRegMarksChange(e: Event) {
    const v = (e.target as HTMLSelectElement).value as RegMarkStyle;
    projectStore.settings.reg_marks = v;
    projectStore.settings.reg_marks_auto = false;
    triggerRecompute();
  }
  function toggleCorner(corner: CornerKey) {
    const set = new Set(projectStore.settings.mark_corners);
    if (set.has(corner)) set.delete(corner);
    else set.add(corner);
    projectStore.settings.mark_corners = (
      ['TL', 'TR', 'BL', 'BR'] as CornerKey[]
    ).filter((c) => set.has(c));
    triggerRecompute();
  }
</script>

<section class="panel">
  <h3>Project</h3>

  <div class="grid two">
    <label>
      <span>Width</span>
      <input
        type="number"
        min="0.1"
        step="0.1"
        bind:value={projectStore.settings.target_width}
        oninput={triggerRecompute}
      />
    </label>
    <label>
      <span>Height</span>
      <input
        type="number"
        min="0.1"
        step="0.1"
        bind:value={projectStore.settings.target_height}
        oninput={triggerRecompute}
      />
    </label>
  </div>
  <button
    class="swap"
    onclick={() => {
      const w = projectStore.settings.target_width;
      projectStore.settings.target_width = projectStore.settings.target_height;
      projectStore.settings.target_height = w;
      triggerRecompute();
    }}>⇅ swap orientation</button
  >

  <div class="grid two">
    <label>
      <span>Units</span>
      <select value={projectStore.settings.units} onchange={onUnitsChange}>
        <option value="in">inches</option>
        <option value="cm">cm</option>
        <option value="mm">mm</option>
      </select>
    </label>
    <label>
      <span>DPI</span>
      <input
        type="number"
        list="dpi-presets"
        min="36"
        max="2400"
        bind:value={projectStore.settings.dpi}
        oninput={triggerRecompute}
      />
      <datalist id="dpi-presets">
        {#each DPI_PRESETS as p (p)}
          <option value={p}></option>
        {/each}
      </datalist>
    </label>
  </div>

  <Slider
    label="Mesh count"
    value={projectStore.settings.mesh_count}
    min={40}
    max={400}
    step={1}
    onchange={(v) => {
      projectStore.settings.mesh_count = v;
      triggerRecompute();
    }}
  />
  <datalist id="mesh-presets">
    {#each MESH_PRESETS as p (p)}
      <option value={p}></option>
    {/each}
  </datalist>

  <p class="meta">
    Output: <strong>{dims.width}×{dims.height}px</strong> &nbsp;·&nbsp; LPI
    ceiling: <strong>{ceiling}</strong>
  </p>

  <h3>Paper &amp; tiling</h3>

  <div class="grid two">
    <label>
      <span>Paper W (in)</span>
      <input
        type="number"
        min="1"
        step="0.5"
        bind:value={projectStore.settings.paper_width_in}
        oninput={triggerRecompute}
      />
    </label>
    <label>
      <span>Paper H (in)</span>
      <input
        type="number"
        min="1"
        step="0.5"
        bind:value={projectStore.settings.paper_height_in}
        oninput={triggerRecompute}
      />
    </label>
  </div>

  <div class="grid two">
    <label>
      <span>Tile mode</span>
      <select
        value={projectStore.settings.tile_mode}
        onchange={(e) => {
          projectStore.settings.tile_mode = (e.target as HTMLSelectElement)
            .value as TileMode;
          triggerRecompute();
        }}
      >
        <option value="auto">auto</option>
        <option value="never">never</option>
      </select>
    </label>
    <label>
      <span>Overlap (in)</span>
      <input
        type="number"
        min="0"
        step="0.05"
        bind:value={projectStore.settings.tile_overlap_in}
        oninput={triggerRecompute}
      />
    </label>
  </div>

  <p class="meta">
    Tiles: <strong>{tiles.cols}×{tiles.rows}</strong>
    ({tiles.tiles.length} sheet{tiles.tiles.length === 1 ? '' : 's'} per layer)
  </p>

  <h3>Output</h3>

  <div class="grid two">
    <label>
      <span>Reg marks</span>
      <select value={projectStore.settings.reg_marks} onchange={onRegMarksChange}>
        <option value="none">none</option>
        <option value="cross">cross</option>
      </select>
    </label>
    <label>
      <span>Mirror</span>
      <select
        value={projectStore.settings.mirror_output}
        onchange={(e) => {
          projectStore.settings.mirror_output = (e.target as HTMLSelectElement)
            .value as MirrorMode;
          triggerRecompute();
        }}
      >
        <option value="none">none</option>
        <option value="horizontal">horizontal</option>
      </select>
    </label>
  </div>

  {#if projectStore.settings.reg_marks !== 'none'}
    <Slider
      label="Mark size (in)"
      value={projectStore.settings.mark_size_in}
      min={0.1}
      max={1.0}
      step={0.05}
      digits={2}
      onchange={(v) => {
        projectStore.settings.mark_size_in = v;
        triggerRecompute();
      }}
    />
    <Slider
      label="Mark margin (in)"
      value={projectStore.settings.mark_margin_in}
      min={0}
      max={1.0}
      step={0.05}
      digits={2}
      onchange={(v) => {
        projectStore.settings.mark_margin_in = v;
        triggerRecompute();
      }}
    />

    <div class="corners">
      <span class="field-label">Corners</span>
      <div class="corner-grid">
        {#each ['TL', 'TR', 'BL', 'BR'] as c (c)}
          <button
            class="corner"
            class:active={projectStore.settings.mark_corners.includes(
              c as CornerKey,
            )}
            onclick={() => toggleCorner(c as CornerKey)}
          >
            {c}
          </button>
        {/each}
      </div>
    </div>
  {/if}

  <div class="color-row">
    <span class="field-label">Substrate</span>
    <ColorPicker
      value={projectStore.settings.substrate_color}
      onchange={(v) => {
        projectStore.settings.substrate_color = v;
        triggerRecompute();
      }}
      ariaLabel="Substrate color"
    />
    <code>{projectStore.settings.substrate_color}</code>
  </div>
</section>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap-m);
  }
  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-dim);
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--text-dim);
  }
  label > input,
  label > select {
    color: var(--text);
    font-size: 13px;
  }
  .grid.two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--gap-s) var(--gap-m);
  }
  .meta {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    margin: 0;
  }
  .meta strong {
    color: var(--text);
    font-weight: 600;
  }
  .corners {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .field-label {
    color: var(--text-dim);
    font-size: 12px;
  }
  .corner-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
  }
  .corner {
    font-family: var(--font-mono);
    font-size: 11px;
    padding: 4px;
  }
  .corner.active {
    background: var(--accent-dim);
    border-color: var(--accent);
    color: var(--bg);
  }
  .color-row {
    display: grid;
    grid-template-columns: max-content auto 1fr;
    align-items: center;
    gap: var(--gap-s);
  }
  .color-row code {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
  }
  .swap {
    align-self: flex-start;
    font-size: 11px;
    padding: 4px 8px;
  }
</style>
